// FILE: src/components/readinessPageHelpers.js
//
// Pure helpers + copy for the redesigned Readiness page. No IO, no React —
// easy to unit-test. The six-input formula itself lives untouched in
// readinessService; everything here is presentation-side derivation.

// Per-metric presentation copy. Weights shown are display-only; the real
// weights ride in on readiness.inputs[key].weight from computeReadiness().
export const METRIC_META = {
  rolling_correctness: {
    title: 'Accuracy',
    focus: 'improving accuracy',
    icon: '\u{1F3AF}',
    summary: 'How you’re scoring lately — recent sessions count heavier.',
    detail: 'A recency-weighted average of your last five sessions. Because newer sessions weigh more, one focused practice this week moves this faster than re-covering old ground.',
    actions: [
      { label: 'Run a focused practice session', kind: 'practice' },
      { label: 'Review what you missed last session', kind: 'plan' },
    ],
  },
  phase_coverage: {
    title: 'Phase coverage',
    focus: 'broadening phase coverage',
    icon: '\u{1F9ED}',
    summary: 'How many phases you’ve proven out — ≥10 attempts at ≥70%.',
    detail: 'Counts phases where you have at least 10 attempts and at least 70% accuracy. Spreading practice into phases you haven’t touched yet lifts this the fastest.',
    actions: [
      { label: 'Practice an untouched phase', kind: 'practice' },
      { label: 'Check your plan’s phase mix', kind: 'plan' },
    ],
  },
  full_sim_count: {
    title: 'Full simulations',
    focus: 'completing a full simulation',
    icon: '\u{1F4CB}',
    summary: 'Full-length sims completed — each one up to the target counts.',
    detail: 'Each full-length simulation (up to the cert’s target) counts toward this. Sims also calibrate your pacing, so they quietly help Time management too.',
    actions: [
      { label: 'Schedule a full simulation', kind: 'plan' },
      { label: 'Start a practice warm-up first', kind: 'practice' },
    ],
  },
  weak_area_improvement: {
    title: 'Weak-area improvement',
    focus: 'lifting your weakest phases',
    icon: '\u{1F331}',
    summary: 'How much your weakest phases moved over recent attempts.',
    detail: 'Measures the shift on your two weakest phases across the last 10 attempts in each. Short, targeted sessions on those exact phases nudge this fastest.',
    actions: [
      { label: 'Target your weakest phase', kind: 'practice' },
      { label: 'Review weak-area resources', kind: 'plan' },
    ],
  },
  recency: {
    title: 'Momentum',
    focus: 'getting a session in this week',
    icon: '⚡',
    summary: 'How fresh your practice is — decays after a few days off.',
    detail: 'Drops roughly 7 points per day away. Even a short session resets it to full — this is the cheapest readiness gain on the board.',
    actions: [
      { label: 'Do a short session today', kind: 'practice' },
    ],
  },
  time_management: {
    title: 'Time management',
    focus: 'sharpening your pacing',
    icon: '⏱',
    summary: 'Sessions finished in time with a passing-floor score.',
    detail: 'The share of sessions you completed without the clock expiring while scoring at least 50%. Full sims are where pacing usually shows up — and where it improves.',
    actions: [
      { label: 'Run a timed session', kind: 'practice' },
    ],
  },
};

export const METRIC_ORDER = Object.keys(METRIC_META);

/** Tone bucket for bars/score chips. */
export function toneFor(value) {
  if (value >= 85) return 'good';
  if (value < 50) return 'warn';
  return 'brand';
}

/**
 * The metric with the most headroom in composite points:
 * gain = weight x (100 - value). Returns null when inputs are missing.
 */
export function biggestOpportunity(readiness) {
  const inputs = readiness?.inputs || {};
  let best = null;
  for (const key of METRIC_ORDER) {
    const item = inputs[key];
    if (!item || typeof item.value !== 'number') continue;
    const weight = typeof item.weight === 'number' ? item.weight : 0;
    const gain = weight * (100 - Math.max(0, Math.min(100, item.value)));
    if (!best || gain > best.gainRaw) {
      best = {
        key,
        meta: METRIC_META[key],
        value: Math.round(item.value),
        weightPct: Math.round(weight * 100),
        gainRaw: gain,
        gain: Math.max(1, Math.round(gain)),
        label: item.label || null,
      };
    }
  }
  return best;
}

/** Friendly mentor-voice message for the coaching card. Kept to one short
 * sentence — the numbers live in the Biggest Opportunity panel below. */
export function coachMessage(opportunity) {
  if (!opportunity) {
    return 'Once a few sessions are in, I’ll point you at the single best place to focus next.';
  }
  return `Your biggest opportunity right now is ${opportunity.meta.focus}.`;
}

/** Rows for the weighted-contribution table. */
export function contributionRows(readiness) {
  const inputs = readiness?.inputs || {};
  const rows = [];
  let total = 0;
  for (const key of METRIC_ORDER) {
    const item = inputs[key];
    if (!item) continue;
    const value = Math.round(item.value || 0);
    const weight = typeof item.weight === 'number' ? item.weight : 0;
    const points = Math.round(weight * (item.value || 0) * 10) / 10;
    total += points;
    rows.push({ key, title: METRIC_META[key].title, weightPct: Math.round(weight * 100), value, points });
  }
  return { rows, total: Math.round(total * 10) / 10 };
}

/**
 * Geometry for the history mini-chart. Returns null with <2 snapshots.
 * Points run oldest -> newest, left -> right.
 */
export function buildSparkline(snapshots, width = 560, height = 120, pad = 10) {
  const rows = (Array.isArray(snapshots) ? snapshots : [])
    .filter((s) => typeof s?.score === 'number' && s?.computed_at)
    .sort((a, b) => new Date(a.computed_at) - new Date(b.computed_at));
  if (rows.length < 2) return null;
  const t0 = new Date(rows[0].computed_at).getTime();
  const t1 = new Date(rows[rows.length - 1].computed_at).getTime();
  const span = Math.max(1, t1 - t0);
  const xFor = (t) => pad + ((t - t0) / span) * (width - pad * 2);
  const yFor = (v) => pad + (1 - Math.max(0, Math.min(100, v)) / 100) * (height - pad * 2);
  const pts = rows.map((s) => ({
    x: Math.round(xFor(new Date(s.computed_at).getTime()) * 10) / 10,
    y: Math.round(yFor(s.score) * 10) / 10,
    score: Math.round(s.score),
  }));
  return {
    pts,
    line: pts.map((p) => `${p.x},${p.y}`).join(' '),
    yFor,
    width,
    height,
    first: rows[0],
    last: rows[rows.length - 1],
  };
}
